// src/services/azureTagsService.js
// Azure Table Storage Service für Tags

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectTags",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Helper functions for data cleaning
const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
};

// Hilfsfunktion für eindeutige IDs
const generateTagId = (name) => {
  return `tag_${name.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
};

// Alle Tags laden
export const getAllTags = async () => {
  try {
    let allTags = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allTags = allTags.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    // Konvertiere Azure Table Format zu Frontend Format
    const tags = allTags.map((item) => ({
      id: item.RowKey,
      name: item.Name,
      description: item.Description || "",
      color: item.Color || "#6c757d",
      category: item.Category || "Allgemein",
      usageCount: item.UsageCount || 0,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));

    return tags;
  } catch (error) {
    console.error("Load Tags Error:", error);
    return [];
  }
};

// Einzelnen Tag laden
export const getTag = async (tagId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='tag',RowKey='${tagId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const item = await response.json();

    return {
      id: item.RowKey,
      name: item.Name,
      description: item.Description || "",
      color: item.Color || "#6c757d",
      category: item.Category || "Allgemein",
      usageCount: item.UsageCount || 0,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    };
  } catch (error) {
    console.error("Load Tag Error:", error);
    return null;
  }
};

// Tag erstellen
export const createTag = async (tagData) => {
  try {
    const tagId = generateTagId(tagData.name);
    const now = new Date().toISOString();

    // Validate required fields
    if (!tagData.name) {
      throw new Error("Tag-Name ist erforderlich");
    }

    // Prüfe ob Tag bereits existiert
    const existingTag = await getTag(tagId);
    if (existingTag) {
      return existingTag; // Gib existierenden Tag zurück
    }

    const entity = {
      PartitionKey: "tag",
      RowKey: tagId,
      Name: cleanValue(tagData.name),
      Description: cleanValue(tagData.description),
      Color: cleanValue(tagData.color || "#6c757d"),
      Category: cleanValue(tagData.category || "Allgemein"),
      UsageCount: 0,
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='tag',RowKey='${tagId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: tagId,
      ...tagData,
      color: tagData.color || "#6c757d",
      category: tagData.category || "Allgemein",
      usageCount: 0,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Create Tag Error:", error);
    throw error;
  }
};

// Tag aktualisieren
export const updateTag = async (tagId, tagData) => {
  try {
    const now = new Date().toISOString();

    // Validate required fields
    if (!tagData.name) {
      throw new Error("Tag-Name ist erforderlich");
    }

    const entity = {
      PartitionKey: "tag",
      RowKey: tagId,
      Name: cleanValue(tagData.name),
      Description: cleanValue(tagData.description),
      Color: cleanValue(tagData.color || "#6c757d"),
      Category: cleanValue(tagData.category || "Allgemein"),
      UsageCount: tagData.usageCount || 0,
      UpdatedAt: now,
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='tag',RowKey='${tagId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return {
      id: tagId,
      ...tagData,
      usageCount: tagData.usageCount || 0,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Update Tag Error:", error);
    throw error;
  }
};

// Tag löschen
export const deleteTag = async (tagId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='tag',RowKey='${tagId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Tag Error:", error);
    throw error;
  }
};

// Tag Usage Count erhöhen
export const incrementTagUsage = async (tagId) => {
  try {
    const tag = await getTag(tagId);
    if (!tag) return;

    await updateTag(tagId, {
      ...tag,
      usageCount: tag.usageCount + 1,
    });
  } catch (error) {
    console.error("Increment Tag Usage Error:", error);
  }
};

// Tag Usage Count verringern
export const decrementTagUsage = async (tagId) => {
  try {
    const tag = await getTag(tagId);
    if (!tag) return;

    await updateTag(tagId, {
      ...tag,
      usageCount: Math.max(0, tag.usageCount - 1),
    });
  } catch (error) {
    console.error("Decrement Tag Usage Error:", error);
  }
};

// Tags nach Kategorie laden
export const getTagsByCategory = async (category) => {
  try {
    const allTags = await getAllTags();
    return allTags.filter((tag) => tag.category === category);
  } catch (error) {
    console.error("Load Tags by Category Error:", error);
    return [];
  }
};

// Tags suchen
export const searchTags = async (searchTerm) => {
  try {
    const allTags = await getAllTags();
    const searchLower = searchTerm.toLowerCase();

    return allTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(searchLower) ||
        tag.description.toLowerCase().includes(searchLower) ||
        tag.category.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error("Search Tags Error:", error);
    return [];
  }
};

// Beliebteste Tags laden
export const getPopularTags = async (limit = 10) => {
  try {
    const allTags = await getAllTags();
    return allTags.sort((a, b) => b.usageCount - a.usageCount).slice(0, limit);
  } catch (error) {
    console.error("Load Popular Tags Error:", error);
    return [];
  }
};

// Vordefinierte Tags erstellen
export const createPredefinedTags = async () => {
  const predefinedTags = [
    {
      name: "Infrastruktur",
      category: "Bereich",
      color: "#007bff",
      description: "Infrastrukturprojekte",
    },
    {
      name: "Städtebau",
      category: "Bereich",
      color: "#28a745",
      description: "Städtebauliche Projekte",
    },
    {
      name: "Architektur",
      category: "Bereich",
      color: "#dc3545",
      description: "Architektonische Projekte",
    },
    {
      name: "Planung",
      category: "Phase",
      color: "#ffc107",
      description: "Planungsphase",
    },
    {
      name: "Neubau",
      category: "Art",
      color: "#17a2b8",
      description: "Neubauprojekte",
    },
    {
      name: "Sanierung",
      category: "Art",
      color: "#6f42c1",
      description: "Sanierungsprojekte",
    },
    {
      name: "Wohnen",
      category: "Nutzung",
      color: "#fd7e14",
      description: "Wohnbauprojekte",
    },
    {
      name: "Büro",
      category: "Nutzung",
      color: "#20c997",
      description: "Bürogebäude",
    },
    {
      name: "Gewerbe",
      category: "Nutzung",
      color: "#e83e8c",
      description: "Gewerbliche Nutzung",
    },
    {
      name: "Öffentlich",
      category: "Träger",
      color: "#6610f2",
      description: "Öffentliche Projekte",
    },
    {
      name: "Privat",
      category: "Träger",
      color: "#6c757d",
      description: "Private Projekte",
    },
  ];

  try {
    const results = [];
    for (const tagData of predefinedTags) {
      const tag = await createTag(tagData);
      results.push(tag);
    }
    return results;
  } catch (error) {
    console.error("Create Predefined Tags Error:", error);
    return [];
  }
};

// Tag finden oder erstellen
export const findOrCreateTag = async (tagName, category = "Allgemein") => {
  try {
    const tagId = generateTagId(tagName);
    let tag = await getTag(tagId);

    if (!tag) {
      tag = await createTag({
        name: tagName,
        category: category,
        description: `Automatisch erstellt: ${tagName}`,
      });
    }

    return tag;
  } catch (error) {
    console.error("Find or Create Tag Error:", error);
    throw error;
  }
};

// Konfiguration aktualisieren
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
